import React, { FunctionComponent, useState, MouseEvent, useEffect } from 'react'
import {
  AppBar,
  Container,
  Toolbar,
  Grid,
  Button,
  useMediaQuery,
  Drawer,
  Box,
  IconButton,
  Menu as MuiMenu,
  MenuItem,
} from '@mui/material'
import { Navigation } from './Navigation'
import { Clear, Menu } from '@mui/icons-material'
import { CustomAccordion } from './Accordion'
import axiosInstance from 'store/axiosInstance'
import { useTranslation } from 'react-i18next'
import { NonResidentLoginDialog } from 'components/purchase/NonResidentLoginDialog'
import { useLogoutMutation } from 'services/auth/logout'
import { useNavigate } from 'react-router-dom'
import LanguageIcon from '@mui/icons-material/Language'
import i18next from 'i18n'
import useLocalStorage from 'hooks/useLocalStorage'

const logo = require('assets/images/new_logo.png')
const digital_login = require('assets/images/digital_login.png')

export const Header: FunctionComponent = () => {
  const isMobile = useMediaQuery('(max-width: 1200px)')
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const [isNonResidentModalOpen, setNonResidentModalOpen] = useState<boolean>(false)
  const { t } = useTranslation()
  const [logout] = useLogoutMutation()
  const navigate = useNavigate()
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null)
  const [lang, setLang] = useLocalStorage('lang', i18next.language)

  const handleLangClick = (event: MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget)
  }

  const handleLangClose = () => {
    setLangAnchorEl(null)
  }

  const handleLangSelect = (lang: string) => {
    void i18next.changeLanguage(lang)
    setLang(lang)
    handleLangClose()
  }

  const toggleDrawer =
    (open: boolean) => (event: React.MouseEvent | React.KeyboardEvent) => {
      setDrawerOpen(open)
    }

  const handleLogin = async () => {
    await logout()
    axiosInstance.get('/auth/login')
  }

  const handleNonResidentLoginClick = () => {
    setNonResidentModalOpen(true)
  }

  const renderDrawer = () => {
    return (
      <Box
        role="presentation"
        onKeyDown={toggleDrawer(false)}
        sx={{
          width: '100vw',
          height: 'calc(100vh - 64px)',
          padding: '24px 0',
        }}
      >
        <Container maxWidth="lg" disableGutters={!isMobile}>
          <Box sx={{ marginTop: '24px' }}>
            <CustomAccordion bg="#ffffff" color="#050f21" />
          </Box>
        </Container>
      </Box>
    )
  }

  useEffect(() => {
    void i18next.changeLanguage(lang)
  }, [lang])

  return (
    <>
      <AppBar
        color="transparent"
        sx={{
          background: '#fff',
        }}
        position="static"
      >
        <Container maxWidth="lg" disableGutters={!isMobile}>
          <Grid container>
            <Grid alignSelf="center" item xs={3} lg={6}>
              <Toolbar
                sx={{
                  padding: '8px 0',
                  cursor: 'pointer',
                }}
                disableGutters={!isMobile}
                onClick={() => navigate('/')}
              >
                <Box
                //   sx={{
                //     animationName: 'rotate',
                //     animationDuration: '5s',
                //     animationIterationCount: 'infinite',
                //     animationTimingFunction: 'linear',
                //   }}
                >
                  <img width="80px" src={logo} alt="Logo" />
                </Box>
              </Toolbar>
            </Grid>
            <Grid
              alignSelf="center"
              item
              xs={9}
              lg={6}
              sx={{
                textAlign: 'right',
              }}
            >
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                flex="1"
              >
                <Button
                  variant="contained"
                  style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                  sx={{
                    shrink: 0,
                    whiteSpace: 'nowrap',
                    mr: 2,
                    borderRadius: '12px',
                    padding: '8px 16px',
                  }}
                  color="primary"
                  onClick={handleNonResidentLoginClick}
                >
                  {t('non-resident-login_foreigner')}
                </Button>
                <Button
                  sx={{
                    mr: 2,
                    width: '139px',
                    '&:hover': { background: 'transparent' },
                  }}
                  onClick={handleLogin}
                >
                  <img width="100%" src={digital_login} alt="Asan Login" />
                </Button>
                <Box mr={1}>
                  <IconButton
                    size="medium"
                    aria-label="Language"
                    aria-controls="menu-language"
                    aria-haspopup="true"
                    onClick={handleLangClick}
                    color="inherit"
                  >
                    <LanguageIcon />
                  </IconButton>
                  <MuiMenu
                    id="menu-language"
                    anchorEl={langAnchorEl}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(langAnchorEl)}
                    onClose={handleLangClose}
                  >
                    <MenuItem
                      sx={{
                        background:
                          i18next.language === 'az' ? 'rgba(0, 0, 0, .2)' : '',
                      }}
                      onClick={() => handleLangSelect('az')}
                    >
                      AZE
                    </MenuItem>
                    <MenuItem
                      sx={{
                        background:
                          i18next.language === 'en' ? 'rgba(0, 0, 0, .2)' : '',
                      }}
                      onClick={() => handleLangSelect('en')}
                    >
                      ENG
                    </MenuItem>
                  </MuiMenu>
                </Box>
                {isMobile && (
                  <IconButton
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    onClick={toggleDrawer(!drawerOpen)}
                  >
                    {drawerOpen ? <Clear /> : <Menu />}
                  </IconButton>
                )}
              </Box>

              <Drawer
                sx={{
                  marginTop: '64px',
                  transform: 'translateY(0)',
                }}
                anchor="top"
                open={drawerOpen}
                onClose={toggleDrawer(false)}
              >
                {renderDrawer()}
              </Drawer>
            </Grid>
          </Grid>
        </Container>
      </AppBar>

      {!isMobile && (
        <AppBar position="static">
          <Container maxWidth="lg" disableGutters={!isMobile}>
            <Toolbar disableGutters={!isMobile} component="nav">
              <Navigation />
            </Toolbar>
          </Container>
        </AppBar>
      )}
      {isNonResidentModalOpen && (
        <NonResidentLoginDialog
          open={isNonResidentModalOpen}
          onClose={() => setNonResidentModalOpen(false)}
        />
      )}
    </>
  )
}
